/**
 * Favourites persistence — saved PatternConfigs.
 *
 * Mirrors the sketchRepository platform split: localStorage on web (with an
 * injectable KeyValueStore for tests), Capacitor Filesystem deferred to the
 * same phase as sketch native persistence.
 */
import type { PatternConfig } from '@/patterns/wallpaper';
import type { KeyValueStore } from './sketchRepository';

const FAVOURITES_KEY = 'esch_favourites';

export interface FavouritesRepository {
  load(): Promise<PatternConfig[]>;
  save(favourites: PatternConfig[]): Promise<void>;
}

export class LocalStorageFavouritesRepository implements FavouritesRepository {
  private storage: KeyValueStore;

  constructor(storage?: KeyValueStore) {
    this.storage = storage ?? globalThis.localStorage;
  }

  async load(): Promise<PatternConfig[]> {
    const raw = this.storage.getItem(FAVOURITES_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as PatternConfig[]) : [];
    } catch {
      return [];
    }
  }

  async save(favourites: PatternConfig[]): Promise<void> {
    this.storage.setItem(FAVOURITES_KEY, JSON.stringify(favourites));
  }
}

let repository: FavouritesRepository | null = null;

export function getFavouritesRepository(): FavouritesRepository {
  if (!repository) {
    // TODO(Phase 9): Capacitor Filesystem on native, alongside sketch persistence.
    repository = new LocalStorageFavouritesRepository();
  }
  return repository;
}

/** Test seam + native bootstrap. */
export function setFavouritesRepository(repo: FavouritesRepository | null): void {
  repository = repo;
}
